<template>
  <div class="pixi-slider">
    <panel
      :height="190"
      :width="width"
      :range="range"
      :labels="gridLabels"
      :issue-name="issueDetails.issue_formatted_description"
      :show-label="showLabel"
      @click-proposal="initializeProposal"
    >
      <grid
        v-if="issueDetails.has_set_initial_proposal"
        :lines="gridPoints"
      ></grid>

      <star
        :value="issueDetails.agreement_value"
        v-if="agreementReached && requires_payment"
      ></star>

      <indicator
        key="opponent"
        v-if="issueDetails.has_set_initial_proposal && issueDetails.other_party_set_initial_proposal"
        v-model:value="counterProposal"
        :text="counterProposal"
        freeze
        color="blue"
      >
        <shade
          v-for="(range, index) in opponentProposalShades"
          :range="range"
          :key="index"
        ></shade>
      </indicator>

      <indicator
        key="opponentAcceptance"
        v-if="issueDetails.counter_acceptance"
        :value="issueDetails.counter_acceptance"
        :text="issueDetails.counter_acceptance"
        :freeze="true"
        sense="bottom"
        color="orange"
      >
        <shade
          v-for="(range, index) in opponentAccShades"
          :range="range"
          :key="index"
        ></shade>
      </indicator>

      <indicator
        ref="propIndicator"
        key="proposal"
        v-if="issueDetails.has_set_initial_proposal || firstProposal"
        v-model:value="proposal"
        :freeze="freeze"
        :text="snapToGrid(proposal)"
        @drag-end="updatedProposal"
        @is-dragging="isProposalDragging = $event"
        @input="proposal = $event;"
        :containment="proposalContainment"
      >
        <shade
          v-for="(range, index) in proposalShades"
          :range="range"
          :key="index"
        ></shade>
      </indicator>

      <indicator
        ref="secretIndicator"
        key="secretAcceptance"
        v-if="secretAcceptance && issueDetails.has_set_initial_proposal"
        v-model:value="secretAcceptance"
        :freeze="freeze || locked"
        :text="snapToGrid(secretAcceptance)"
        @drag-end="updatedSecretAcc"
        @is-dragging="isSecretDragging = $event"
        @input="secretAcceptance = $event"
        :containment="acceptanceContainment"
        sense="bottom"
        color="yellow"
      >
        <shade
          v-for="(range, index) in secretAccShades"
          :range="range"
          :key="index"
        ></shade>
      </indicator>

    </panel>

    <!-- Modal Components -->
    <acceptance-modal
      v-if="modalsOpen.acceptance"
      v-model:acceptance="secretAcceptance"
      v-model:value="modalsOpen.acceptance"
      :issueid="issueDetails.issue_id"
      :grid-points="gridPoints"
      :ask-for-mediator-perms="askForMediatorPerms"
      :last-position="issueDetails['acceptance']"
      :session-number="sessionNumber"
      :other-party-ended-or-done-session="otherPartyEndedOrDoneSession"
      @ok="confirmedSecretAcc"
      @cancel="secretAcceptance = issueDetails['acceptance'];modalsOpen.acceptance = false"
    ></acceptance-modal>

    <initial-prop-modal
      v-if="modalsOpen.initialProposal"
      v-model:value="modalsOpen.initialProposal"
      v-model:proposal="proposal"
      :issueid="issueDetails.issue_id"
      :grid-points="gridPoints"
      @ok="confirmedInitialProposal"
      @cancel="cancelInitialProp"
    ></initial-prop-modal>

    <proposal-modal
      v-if="modalsOpen.proposal"
      v-model:value="modalsOpen.proposal"
      v-model:proposal="proposal"
      :issueid="issueDetails.issue_id"
      :grid-points="gridPoints"
      :last-position="issueDetails['visible_proposal']"
      @ok="$emit('proposal', proposal);modalsOpen.proposal = false"
      @cancel="proposal = issueDetails['visible_proposal'];modalsOpen.proposal = false"
    ></proposal-modal>

    <proposal-and-acceptance-modal
      v-if="modalsOpen.both"
      v-model:value="modalsOpen.both"
      v-model:proposal="proposal"
      :issueid="issueDetails.issue_id"
      @ok="confirmedBoth"
      @cancel="proposal = issueDetails['visible_proposal'];modalsOpen.both = false"
    ></proposal-and-acceptance-modal>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, useTemplateRef} from 'vue';
import Panel from 'components/panel';
import Indicator from 'components/indicator';
import Shade from 'components/shade';
import Grid from 'components/grid';
import Star from 'components/star';
import AcceptanceModal from 'components/modals/update_acceptance';
import InitialPropModal from 'components/modals/initial_proposal';
import ProposalModal from 'components/modals/update_proposal';
import ProposalAndAcceptanceModal from 'components/modals/update_both';
import { closestPointInGrid, getLabelCount } from 'utilities/grid';

export default {
  name: 'Slider',
  components: {Panel, Grid, Indicator, Star, AcceptanceModal, Shade, InitialPropModal, ProposalModal, ProposalAndAcceptanceModal},
  props: {
    issueDetails: Object,
    agreementReached: Boolean,
    askForMediatorPerms: Boolean,
    hasEndedSession: String,
    freeze: Boolean,
    locked: Boolean,
    width: Number,
    showLabel: Boolean,
    requires_payment: Boolean,
    otherPartyEndedOrDoneSession: Boolean,
    sessionNumber: Number
  },
  emits: ['proposal', 'both', 'acceptance', 'allow-mediator-to-see-secret', 'asks-to-end-session'],
  setup(props, { emit }) {
    // Reactive references
    const proposal = ref(120);
    const propIndicator = useTemplateRef('propIndicator')
    const secretIndicator = useTemplateRef('secretIndicator')
    const secretAcceptance = ref(120);
    const zoomFactor = ref(1);
    const modalsOpen = ref({
      acceptance: false,
      initialProposal: false,
      proposal: false,
      both: false
    });
    const firstProposal = ref(false);
    const isProposalDragging = ref(false);
    const isSecretDragging = ref(false);

    const isModalOpen = computed(() => {
      return modalsOpen.value.acceptance      ||
             modalsOpen.value.initialProposal ||
             modalsOpen.value.proposal        ||
             modalsOpen.value.both    
    });
    
    const isDragging = computed(() => 
    {
      return (isProposalDragging || isSecretDragging)
    });
    const range = computed(() => [props.issueDetails.unpreferred_value, props.issueDetails.preferred_value]);
    const fromUnit = computed(() => Number(range.value[0]));
    const counterProposal = computed(() => {
      return Number(props.issueDetails['counter_proposal']) || fromUnit.value
    });
    const toUnit = computed(() => Number(range.value[1]));
    const proposalContainment = computed(() => {
      return [
        (props.locked ? props.issueDetails.acceptance : props.issueDetails.counter_proposal) || fromUnit.value,
        toUnit.value
      ]
    });
    const acceptanceContainment = computed(() => {
      return [
        props.issueDetails.counter_proposal || fromUnit.value,
        props.issueDetails.visible_proposal || toUnit.value
      ]
    });
    const isHightestValueOnTheRight = computed(() => {
      return fromUnit.value < toUnit.value
    });
    const isHightestValueOnTheLeft = computed(() => {
      return fromUnit.value > toUnit.value
    });
    const secretAccShades = computed(() => {
      let moves = [...historicalMoves.value.acceptance]

      // Initial proposal represents the first secret move (Add it to the beginning of the moves)
      if (props.issueDetails.has_set_initial_proposal)
        moves.unshift(props.issueDetails.initial_proposal)

      // According to graphic in "two-level history shading" PT story
      // Link to graphic: https://imgur.com/5GSHvf1
      if (props.hasEndedSession === 'yes')
        moves = [toUnit.value, ...moves.slice(-2)]  //      Joe w committed secret
      else
        moves = [toUnit.value, ...moves.slice(-3, -1)]  //  Joe w uncommitted secret

      removeDuplicates(moves)
      return getRangesBetweenMoves(moves).reverse()
    });
    const opponentAccShades = computed(() => {
      if ( !historicalMoves.value.counter_acceptance )
        return []

      // Same algorithm as in secretAccShades() for uncommited secret
      let moves = [...historicalMoves.value.counter_acceptance]
      if (typeof props.issueDetails.counter_initial_proposal === 'number')
        moves.unshift(props.issueDetails.counter_initial_proposal)
      moves = [fromUnit.value, ...moves.slice(-3, -1)]
      removeDuplicates(moves)
      return getRangesBetweenMoves(moves).reverse()
    });
    const historicalMoves = computed(() => {
      if ( !props.issueDetails.historical_moves )
        return { visible_proposal: [], acceptance: [], counter_proposal: [], counter_acceptance: [] }
      return props.issueDetails.historical_moves
    });
    const proposalShades = computed(() => {
      if (typeof props.issueDetails.visible_proposal !== 'number')
        return []

      let moves = [props.issueDetails.visible_proposal]
      let allMoves = historicalMoves.value.visible_proposal
      let pos = props.hasEndedSession === 'yes' ? 2 : 3
      let res = false
      if (allMoves)
        res = allMoves[allMoves.length-pos]
      let thirdToLast = res
      if (thirdToLast)
        moves.push(thirdToLast)
      moves.push(toUnit)

      removeDuplicates(moves)
      return getRangesBetweenMoves(moves)
    });
    const opponentProposalShades = computed(() => {
      if (typeof props.issueDetails.counter_proposal !== 'number')
        return []
      let res = false
      let moves = [props.issueDetails.counter_proposal]
      let allMoves = historicalMoves.value.counter_proposal
      let pos = props.hasEndedSession === 'yes' ? 2 : 3
      if (allMoves)
        res = allMoves[allMoves.length-pos]
      let thirdToLast = res
      if (thirdToLast)
        moves.push(thirdToLast)
      moves.push(fromUnit.value)

      removeDuplicates(moves)
      return getRangesBetweenMoves(moves)
    });
    const gridPoints = computed(() => props.issueDetails.suggestion_set.map(Number) || []);
    const gridLabels = computed(() => {
      const labels = [];
      const lc = getLabelCount(gridPoints.value.length - 1);
      let value = 0;
      for (let i = 0; i < gridPoints.value.length; i++) {
        value = gridPoints.value[i];
        if (i % lc === 0 || value === fromUnit.value || value === toUnit.value) {
          labels.push(value);
        }
      }
      return labels;
    });

    // Methods
    const initializeProposal = (x) => {
      if (props.issueDetails.has_set_initial_proposal) {
        return props.issueDetails.has_set_initial_proposal
      }
      firstProposal.value = true;

      nextTick().then(() => {
        // Drag proposal logic here (use `proposal.value` directly)
        propIndicator.value.dragTo(x);
      });
    };
    const removeDuplicates = (values) => {
      let removeIndexes = []
      for (let i=values.length-1; i > 0; i--) {
        if (values[i-1] === values[i]) {
          removeIndexes.push(i)
        }
      }
      for (let i of removeIndexes) {
        values.splice(i, 1)
      }
      return values
    };
    const getRangesBetweenMoves = (moves) => {
      // E.g. if moves = [120, 110, 95]
      // then this returns [ [120, 110], [110, 95] ]
      // Or if moves = [20, 30, 40] -> [ [20, 30], [30, 40] ]
      let ranges = []
      let from = 0
      let to = 0

      for (let i = 0; i+1 < moves.length; i++) {
        from = moves[i]
        to = moves[i+1]
        ranges.push([from, to])
      }

      return ranges
    };
    const updatedProposal = async () => {
      proposal.value = snapToGrid(proposal.value);
      if (proposal.value === props.issueDetails['visible_proposal'] && props.issueDetails.has_set_initial_proposal) return;

      if (!props.issueDetails.has_set_initial_proposal) {
        modalsOpen.value.initialProposal = true;
      } else if (proposal.value >= secretAcceptance.value && isHightestValueOnTheRight.value
          || proposal.value <= secretAcceptance.value && isHightestValueOnTheLeft.value ) {
        modalsOpen.value.proposal = true;
      } else {
        modalsOpen.value.both = true;
      }
    };
    const updatedSecretAcc = async () => {
      secretAcceptance.value = snapToGrid(secretAcceptance.value)

      if (secretAcceptance.value === props.issueDetails['acceptance'])
        return  // If move is not valid

      modalsOpen.value.acceptance = true
    };
    const confirmedInitialProposal = () => {
      secretAcceptance.value = proposal.value;
      modalsOpen.value.initialProposal = false
      emit('proposal', proposal.value);
    };
    const confirmedBoth = () => {
      secretAcceptance.value = proposal.value
      modalsOpen.value.both = false
      emit('both', proposal.value)
    };
    const confirmedSecretAcc = (event) => {
      modalsOpen.value.acceptance = false
      if (event.endSession)
        emit('asks-to-end-session')
      if (event.allowMediatorToSeeSecret)
        emit('allow-mediator-to-see-secret')
      emit('acceptance', secretAcceptance.value)
    };
    const cancelInitialProp = () => {
      firstProposal.value = false;
      proposal.value = toUnit.value;
      modalsOpen.value.initialProposal = false;
    };

    const snapToGrid = (position) => {
      let gridPointsExtended = [...gridPoints.value];
      let pos = position
      gridPointsExtended.sort((a, b) => a - b);
      return closestPointInGrid(pos, gridPointsExtended);
    };
    const isOnGrid = (position) => {
      return gridPoints.value.includes(position)
    };
    const zoomOnGrid = () => {
      // Use case:
      // <panel @double-tap="zoomOnGrid">

      if (zoomFactor < 4)
        zoomFactor *= 2
      else
        zoomFactor = 1
    };

    // Watchers
    watch(() => props.issueDetails, (newValue, oldValue) => {
      if ((isModalOpen.value != true) && (isDragging.value != true)) {
        setFlagsToFitData();
      }
    });

    watch(() => modalsOpen.value, (newVal) => {
      if (!newVal.initialProposal) {
        firstProposal.value = false;
      }
    });

    const setFlagsToFitData = () => {
      proposal.value = props.issueDetails['visible_proposal'] || toUnit.value;
      secretAcceptance.value = props.issueDetails['acceptance'];
    };

    // Lifecycle Hooks
    onMounted(() => {
      setFlagsToFitData();
    });

    return {
      proposal,
      isModalOpen,
      secretAcceptance,
      historicalMoves,
      zoomFactor,
      modalsOpen,
      firstProposal,
      isProposalDragging,
      isHightestValueOnTheRight,
      isHightestValueOnTheLeft,
      isDragging,
      isSecretDragging,
      confirmedSecretAcc,
      proposalContainment,
      acceptanceContainment,
      confirmedBoth,
      updatedSecretAcc,
      range,
      fromUnit,
      counterProposal,
      toUnit,
      gridPoints,
      proposalShades,
      opponentProposalShades,
      gridLabels,
      initializeProposal,
      updatedProposal,
      confirmedInitialProposal,
      cancelInitialProp,
      snapToGrid,
      secretAccShades,
      opponentAccShades,
      setFlagsToFitData
    };
  }
};
</script>

<style scoped>
p {
  font-size: 2em;
  text-align: center;
}
</style>
